/**
 * AddonsSection — Settings → Subscription & billing ▸ Add-ons.
 *
 * The generic, registry-driven successor to the single Garment OS card: it maps
 * over `ADDON_CATALOG` (@flint/api-contract/addons) and renders one toggle row
 * per available add-on (Garment OS, POS, …), showing which the workspace has
 * installed. Flipping a row calls `billing.setAddon`, which upserts the
 * `company_addons` row and recomputes entitlements — the new modules' nav + gate
 * apply immediately (we invalidate the session query so the sidebar updates).
 *
 * Adding a future add-on needs no change here — just a registry entry.
 *
 * Owner-only (`billing.manage`) and write-gated: non-Owners and read-only
 * workspaces see each add-on's state as a static pill, not an interactive toggle.
 */
import * as React from "react";
import {
  Shirt,
  Store,
  Sparkles,
  MessageCircle,
  CalendarClock,
  FolderOpen,
  FileBarChart,
  BookOpenCheck,
  Wallet,
  ArrowLeftRight,
  Palette,
  Plus,
  Loader2,
  Check,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { cn, StatusPill, toast, ConfirmDialog } from "@flint/ui";
import {
  ADDON_CATALOG,
  formatAddonPrice,
  type AddonCatalogEntry,
  type AddonIcon,
} from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { meQueryKey } from "../../../lib/session.ts";
import { AddonIllustration } from "./addon-illustrations.tsx";
import { FormCard } from "../form-card.tsx";

const ADDON_ICONS: Record<AddonIcon, LucideIcon> = {
  shirt: Shirt,
  store: Store,
  sparkles: Sparkles,
  "message-circle": MessageCircle,
  "calendar-clock": CalendarClock,
  "folder-open": FolderOpen,
  "file-bar-chart": FileBarChart,
  "book-open-check": BookOpenCheck,
  wallet: Wallet,
  "arrow-left-right": ArrowLeftRight,
  palette: Palette,
};

/**
 * The per-row action. A paid add-on reads as a purchase, not a switch: when off
 * it's an "Add · ₹price/mo" button (disabled-with-reason when there's no active
 * plan to bill against); when on it shows an "Added" marker + a "Remove" button.
 */
function AddonAction({
  addon,
  checked,
  price,
  canManage,
  writeEnabled,
  needsActivePlan,
  trialAddon,
  pending,
  onAdd,
  onRemove,
}: {
  addon: AddonCatalogEntry;
  checked: boolean;
  price: string | null;
  canManage: boolean;
  writeEnabled: boolean;
  needsActivePlan: boolean;
  trialAddon: boolean;
  pending: boolean;
  onAdd: () => void;
  onRemove: () => void;
}) {
  // Non-owners / read-only workspaces see state, not controls.
  if (!canManage || !writeEnabled) {
    return (
      <span className="text-xs text-muted" title={canManage ? "Read-only workspace" : "Owner only"}>
        {canManage ? "Read-only" : "Owner only"}
      </span>
    );
  }

  // Paid add-on enabled during trial — show as included, not purchased.
  // No Remove button: the add-on is part of the trial experience.
  if (trialAddon) {
    return (
      <span className="text-xs font-medium text-sky-600">Included in trial</span>
    );
  }

  if (checked) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-xs font-medium text-ok">
          <Check size={13} strokeWidth={2.5} aria-hidden /> Added
        </span>
        <button
          type="button"
          disabled={pending}
          onClick={onRemove}
          className="inline-flex h-8 items-center rounded-sm border border-line px-2.5 text-xs font-medium text-ink-2 hover:border-bad/40 hover:text-bad disabled:opacity-60"
        >
          {pending ? <Loader2 size={13} className="animate-spin" aria-hidden /> : "Remove"}
        </button>
      </div>
    );
  }

  // Off — the purchase button. Disabled (with reason) when there's no plan to bill.
  const label = price ? `Add · ${price}` : "Add";
  return (
    <button
      type="button"
      disabled={pending || needsActivePlan}
      onClick={onAdd}
      title={needsActivePlan ? "Activate a paid plan first" : `Add ${addon.name}`}
      aria-label={`Add ${addon.name}`}
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-sm px-3 text-xs font-semibold transition-colors",
        "focus-visible:outline-2 focus-visible:outline-sky-600 focus-visible:outline-offset-2",
        needsActivePlan
          ? "cursor-not-allowed bg-bg text-muted ring-1 ring-line"
          : "bg-ink text-paper hover:bg-ink-2 disabled:opacity-60",
      )}
    >
      {pending ? (
        <Loader2 size={13} className="animate-spin" aria-hidden />
      ) : (
        <Plus size={13} strokeWidth={2.5} aria-hidden />
      )}
      {pending ? "Adding…" : label}
    </button>
  );
}

export interface AddonsSectionProps {
  /** Installed-and-enabled add-on keys, from `billing.overview`. */
  enabledAddons: string[];
  /** Write gate — read-only workspaces can't toggle (but still see state). */
  writeEnabled: boolean;
  /**
   * Whether the workspace has an active (paying) plan. Paid add-ons bill on the
   * next invoice, so they can only be switched on once there's an active mandate
   * — until then the toggle is disabled-with-reason rather than failing on click.
   */
  planActive: boolean;
}

export function AddonsSection({ enabledAddons, writeEnabled, planActive }: AddonsSectionProps) {
  const canManage = usePermission("billing.manage");

  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const [pendingKey, setPendingKey] = React.useState<string | null>(null);
  // The paid add-on awaiting purchase confirmation. Adding one charges money
  // (prorated immediately), so we never fire the mutation straight off the click.
  const [confirmAddon, setConfirmAddon] = React.useState<AddonCatalogEntry | null>(null);

  const setAddon = trpc.billing.setAddon.useMutation({
    onSuccess: (res) => {
      const entry = ADDON_CATALOG.find((a) => a.key === res.addonKey);
      const name = entry?.name ?? res.addonKey;
      const paid = entry?.pricePaiseMonthly != null;
      if (res.changed) {
        toast.success(res.enabled ? `${name} added` : `${name} removed`, {
          description: res.enabled
            ? paid
              ? "It's now active across the workspace and billed on your next invoice."
              : "Its features are now available across the workspace."
            : "Its features are now hidden for this workspace.",
        });
      }
      void utils.billing.overview.invalidate();
      // The sidebar reads modules from the session query (a distinct cache key)
      // — invalidate it so the add-on's nav appears/disappears immediately.
      void queryClient.invalidateQueries({ queryKey: meQueryKey });
    },
    onError: (err) => {
      toast.error("Couldn't update add-on", { description: err.message });
    },
    onSettled: () => setPendingKey(null),
  });

  const enabled = new Set(enabledAddons);
  // Only count add-ons that are genuinely purchased (not trial-included).
  // A paid add-on enabled on a non-active plan is part of the trial, not a purchase.
  const trialCount = ADDON_CATALOG.filter(
    (a) => enabled.has(a.key) && a.pricePaiseMonthly != null && !planActive,
  ).length;
  const activeCount = ADDON_CATALOG.filter(
    (a) => enabled.has(a.key) && !(a.pricePaiseMonthly != null && !planActive),
  ).length;

  function setEnabled(addonKey: string, next: boolean) {
    setPendingKey(addonKey);
    setAddon.mutate({ addonKey, enabled: next });
  }

  /** Clicking "Add" on a PAID add-on opens the purchase confirmation first; a
   *  free add-on (no price, no charge) is applied straight away. */
  function requestAdd(addon: AddonCatalogEntry) {
    if (addon.pricePaiseMonthly != null) setConfirmAddon(addon);
    else setEnabled(addon.key, true);
  }

  /** Confirmed purchase — fire the enable, await so the dialog shows progress;
   *  the onError toast covers failure so we let the dialog close either way. */
  async function confirmPurchase() {
    if (!confirmAddon) return;
    setPendingKey(confirmAddon.key);
    try {
      await setAddon.mutateAsync({ addonKey: confirmAddon.key, enabled: true });
    } catch {
      // onError already surfaced a toast; let the dialog close.
    }
  }

  const confirmPrice = confirmAddon ? formatAddonPrice(confirmAddon) : null;

  return (
    <>
    <FormCard
      icon={<Store size={14} strokeWidth={2} aria-hidden />}
      title="Add-ons"
      description="Optional modules you can add to this workspace — billed on your next invoice."
      badge={
        <StatusPill tone={activeCount > 0 ? "ok" : trialCount > 0 ? "sky" : "muted"} size="xs">
          {activeCount > 0
            ? `${activeCount} active`
            : trialCount > 0
              ? `${trialCount} in trial`
              : "None active"}
        </StatusPill>
      }
      collapsible
      bodyClassName="p-0"
    >
      <div className="divide-y divide-line">
        {ADDON_CATALOG.map((addon) => {
          // A paid add-on needs an active plan to bill against. When there's no
          // active plan, the "Add" button is disabled-with-reason (never a dead
          // click). A paid add-on that's already enabled on a non-active plan is
          // a trial inclusion — shown differently from a purchased add-on.
          const isPaid = addon.pricePaiseMonthly != null;
          const isEnabled = enabled.has(addon.key);
          const needsActivePlan = isPaid && !planActive && !isEnabled;
          const trialAddon = isPaid && !planActive && isEnabled;
          return (
            <AddonRow
              key={addon.key}
              addon={addon}
              checked={isEnabled}
              canManage={canManage}
              writeEnabled={writeEnabled}
              needsActivePlan={needsActivePlan}
              trialAddon={trialAddon}
              pending={pendingKey === addon.key}
              onAdd={() => requestAdd(addon)}
              onRemove={() => setEnabled(addon.key, false)}
            />
          );
        })}
      </div>
      {/* Trust line — the WhatsApp add-on gates BUSINESS messaging only; security
          codes are never part of an add-on. Reassures the founder they can't lock
          themselves out by turning WhatsApp off. */}
      <div className="flex items-start gap-2 border-t border-line px-5 py-3">
        <ShieldCheck size={13} className="mt-0.5 shrink-0 text-muted" aria-hidden />
        <p className="text-xs text-muted">
          Login codes & security alerts always reach you by SMS — they're never part of an add-on.
        </p>
      </div>
    </FormCard>

      {/* Purchase confirmation — adding a paid add-on charges money (prorated
          immediately), so we confirm the amount + what happens before firing. */}
      <ConfirmDialog
        open={confirmAddon != null}
        onOpenChange={(open) => {
          if (!open) setConfirmAddon(null);
        }}
        media={confirmAddon ? <AddonIllustration addonKey={confirmAddon.key} /> : null}
        title={confirmAddon ? `Add ${confirmAddon.name}?` : ""}
        description={
          confirmAddon ? (
            <>
              <span className="font-medium text-ink">{confirmAddon.name}</span> ({confirmPrice}) will be
              added to your subscription. You'll be charged a prorated amount for the rest of this
              billing cycle now, then {confirmPrice} on each invoice. You can remove it anytime — you
              keep it until the cycle you've paid for ends.
            </>
          ) : (
            ""
          )
        }
        footnote="Payments are processed securely by Razorpay through your saved auto-pay mandate (UPI AutoPay or card). Flint never sees or stores your card details."
        confirmLabel={confirmPrice ? `Add · ${confirmPrice}` : "Add"}
        onConfirm={confirmPurchase}
      />
    </>
  );
}

function AddonRow({
  addon,
  checked,
  canManage,
  writeEnabled,
  needsActivePlan,
  trialAddon,
  pending,
  onAdd,
  onRemove,
}: {
  addon: AddonCatalogEntry;
  checked: boolean;
  canManage: boolean;
  writeEnabled: boolean;
  needsActivePlan: boolean;
  trialAddon: boolean;
  pending: boolean;
  onAdd: () => void;
  onRemove: () => void;
}) {
  const Icon = ADDON_ICONS[addon.icon];
  const price = formatAddonPrice(addon);
  return (
    <div className="flex items-start gap-3 px-5 py-4">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-bg text-muted ring-1 ring-line">
        <Icon size={16} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-ink">{addon.name}</p>
          {price ? <span className="text-xs font-semibold tabular-nums text-ink">{price}</span> : null}
          <StatusPill tone={trialAddon ? "sky" : checked ? "ok" : "muted"} size="xs">
            {trialAddon ? "Trial" : checked ? "On" : "Off"}
          </StatusPill>
        </div>
        <p className="mt-0.5 text-xs text-muted">{addon.description}</p>
        {trialAddon ? (
          <p className="mt-1 text-xs text-muted">Included during your trial — activate a paid plan to keep it.</p>
        ) : price && checked ? (
          <p className="mt-1 text-xs text-muted">Billed on your next invoice while active.</p>
        ) : needsActivePlan ? (
          <p className="mt-1 text-xs text-muted">Activate a paid plan to add this.</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-2.5 pt-0.5">
        <AddonAction
          addon={addon}
          checked={checked}
          price={price}
          canManage={canManage}
          writeEnabled={writeEnabled}
          needsActivePlan={needsActivePlan}
          trialAddon={trialAddon}
          pending={pending}
          onAdd={onAdd}
          onRemove={onRemove}
        />
      </div>
    </div>
  );
}
